import React, { forwardRef, useEffect, useState } from 'react'
import { CustomModal, BuySellDetailsMRT } from '@components'
import {
    Box,
    FormControl,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Select,
    Typography,
    MenuItem
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { apiBuyContract, apiGetOrder } from '@store';
import { showToast, getMargin } from '@utils';

export const Sell = forwardRef(
    ({ handleClose, selectedContract, user, sym, open, chf }, ref) => {
        const dispatch = useDispatch();
        const { hedgeMargin } = useSelector((state) => state.hedgeMargin);

        const [state, setState] = useState({
            market: 'TTF',
            type: 'sell',
            price_selected: '',
            seller: user?.CompanyShort,
            buyer: '',
            deliverypoint: 'TTF',
            volume: '',
            exitpoint: '',
            price_percent: '100',
            ordertype: 'internal',
            currency: 'CHF',
            sym: selectedContract?.contract || sym
        });

        const [margin, setMargin] = useState(0);
        const [submitting, setSubmitting] = useState(false);

        useEffect(() => {
            setMargin(
                getMargin({
                    contractName: selectedContract?.contract || sym,
                    hedgeMargin,
                    volume: state.volume
                })
            );
        }, [selectedContract, sym, hedgeMargin, state.volume]);

        useEffect(() => {
            if (user?.CanSellTo?.length > 0) {
                setState((prevState) => ({
                    ...prevState,
                    buyer: user.CanSellTo[0]
                }));
            }
        }, [user]);

        const handleChange = (e) => {
            const { name, value } = e.target;
            setState({
                ...state,
                [name]: value
            });
        };

        const [type, setType] = useState('bid');

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (submitting) return;
            setSubmitting(true);
            const volume = parseFloat(state.volume);
            const bid = parseFloat(selectedContract.bid);

            const data = {
                ...state,
                margin: margin,
                total_price: margin + bid,
                volume,
                exitpoint: state.exitpoint || {},
                price: { bid: bid }
            };

            const resp = await dispatch(apiBuyContract(data));
            if (resp?.meta?.requestStatus === 'fulfilled') {
                handleClose();
                dispatch(apiGetOrder());
                showToast('success', 'Sales Order Saved');
            } else if (resp?.meta?.requestStatus === 'rejected') {
                showToast('error', resp?.payload || 'Error buying contract');
            }
            setSubmitting(false);
        };

        // timer
        const [timer, setTimer] = useState(25);

        useEffect(() => {
            const interval = setInterval(() => {
                setTimer((timer) => timer - 1);
            }, 1000);
            return () => clearInterval(interval);
        }, [timer]);

        useEffect(() => {
            if (timer === 0) {
                handleClose();
            }
        }, [timer, handleClose]);

        useEffect(() => {
            if (ref?.current) {
                setTimer(25);
            }
        }, [ref]);

        return (
            <CustomModal
                handleClose={handleClose}
                handleSubmit={handleSubmit}
                timer={timer}
                modalTitle={`Sell Contract`}
                modalTitleColor={'#EB6969'}
                buttonText={'Sell'}
                className="modal-button-danger"
                disabled={submitting || (state.volume ? false : true)}
                ref={ref}
            >
                <Box className="flex flex-col gap-3">
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Please Select what you would like to sell
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Age"
                            name="type"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                        >
                            <MenuItem value="bid">Bid Price</MenuItem>
                        </Select>
                    </FormControl>
                    <Box className="flex gap-3">
                        <Typography>Contract:</Typography>
                        <Typography color={'black'}>
                            {selectedContract?.contract || sym}
                        </Typography>
                    </Box>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel
                            id="contractLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Volume
                        </InputLabel>
                        <OutlinedInput
                            id="volume"
                            label="Volume"
                            name="volume"
                            value={state.volume}
                            onChange={handleChange}
                            inputProps={{
                                step: 0.01,
                                min: 0
                            }}
                            required
                            endAdornment={
                                <InputAdornment position="start">
                                    MW
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Endpoint
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Endpoint"
                            name="exitpoint"
                            value={state.exitpoint}
                            onChange={handleChange}
                        >
                            {user?.Endpoints?.map((endpoint, index) => (
                                <MenuItem key={index} value={endpoint}>
                                    {' '}
                                    {endpoint.name} - {endpoint.location}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Sell To
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Sell To"
                            name="buyer"
                            value={state?.buyer}
                            onChange={handleChange}
                        >
                            {user?.CanSellTo?.map((seller, index) => (
                                <MenuItem key={index} value={seller}>
                                    {seller}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth variant="outlined" disabled>
                        <InputLabel
                            id="deliverypointLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Delivery Point
                        </InputLabel>
                        <OutlinedInput
                            id="deliverypoint"
                            label="Delivery Point"
                            name="deliverypoint"
                            value={state.deliverypoint || 'TTF'}
                        />
                    </FormControl>
                    <BuySellDetailsMRT
                        price={selectedContract?.bid}
                        margin={margin || 0}
                        totalPrice={(parseFloat(selectedContract?.bid) - margin).toFixed(3)}
                        currency="CHF"
                        deliveryPoint={state.deliverypoint}
                        contractHours={selectedContract?.contract_hours}
                        notionalValue={state?.volume ? (selectedContract?.contract_hours * (parseFloat(selectedContract?.bid) - margin) * parseFloat(state.volume)).toFixed(3) : 0}
                        lastUpdated={selectedContract?.updated_at_cet_str}
                    />
                </Box>
            </CustomModal>
        );
    }
);
